import { useMemo } from 'react';
import {
  CurrencyDollarIcon,
  TrashIcon,
  PencilSimpleIcon,
  XIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ProhibitIcon,
} from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SuggestedActionResponse } from '@erp/rpc';
import type { SuggestedActionWithStatus } from './types';

interface SuggestedActionCardProps {
  action: SuggestedActionWithStatus;
  onApprove: (action: SuggestedActionWithStatus) => void;
  onReject: (action: SuggestedActionWithStatus) => void;
  onDismiss: (action: SuggestedActionWithStatus) => void;
  isApplying?: boolean;
  className?: string;
}

export function SuggestedActionCard({
  action,
  onApprove,
  onReject,
  onDismiss,
  isApplying = false,
  className,
}: SuggestedActionCardProps) {
  const gt = useGT();

  const actionTypeConfig = useMemo(
    (): Record<
      SuggestedActionResponse['actionType'],
      {
        icon: typeof CurrencyDollarIcon;
        label: string;
        iconColor: string;
        bgColor: string;
        borderColor: string;
      }
    > => ({
      adjust_line_item_price: {
        icon: CurrencyDollarIcon,
        label: gt('Price Adjustment', {
          context: 'AI suggested order action type label',
        }),
        iconColor: 'text-amber-600 dark:text-amber-400',
        bgColor: 'bg-amber-50 dark:bg-amber-950',
        borderColor: 'border-amber-200 dark:border-amber-800',
      },
      remove_line_item: {
        icon: TrashIcon,
        label: gt('Remove Line Item', {
          context: 'AI suggested order action type label',
        }),
        iconColor: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-950',
        borderColor: 'border-red-200 dark:border-red-800',
      },
      update_header_field: {
        icon: PencilSimpleIcon,
        label: gt('Update Field', {
          context: 'AI suggested order action type label',
        }),
        iconColor: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-50 dark:bg-purple-950',
        borderColor: 'border-purple-200 dark:border-purple-800',
      },
    }),
    [gt],
  );

  const config = actionTypeConfig[action.actionType];

  if (!config) {
    // Unknown action type — skip until frontend is updated
    return null;
  }

  const Icon = config.icon;

  if (action.status !== 'pending') {
    return null;
  }

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 rounded-lg border p-3',
        config.bgColor,
        config.borderColor,
        className,
      )}
    >
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDismiss(action)}
        disabled={isApplying}
        className="absolute right-1 top-1 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
        aria-label={gt('Dismiss', {
          context: 'dismiss AI suggestion card',
        })}
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>

      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          config.bgColor,
        )}
      >
        <Icon className={cn('h-4 w-4', config.iconColor)} />
      </div>

      <div className="min-w-0 flex-1 pr-4">
        <span className="text-xs font-medium text-muted-foreground">
          {config.label}
        </span>
        <p className="mt-0.5 text-sm text-foreground">{action.description}</p>

        <div className="mt-2 flex gap-1.5">
          <Button
            size="sm"
            variant="default"
            onClick={() => onApprove(action)}
            disabled={isApplying}
            className="h-7 px-2 text-xs"
          >
            {isApplying ? (
              <CircleNotchIcon className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <CheckCircleIcon className="mr-1 h-3 w-3" />
            )}
            {gt('Approve', { context: 'approve AI suggested order change' })}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReject(action)}
            disabled={isApplying}
            className="h-7 px-2 text-xs"
          >
            <ProhibitIcon className="mr-1 h-3 w-3" />
            {gt('Reject', { context: 'reject AI suggested order change' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
